import type { DocumentNode } from "graphql";

import type { ApolloCache } from "util/graphql";
import { updateDocumentNode } from "common/meeting/util";

type RemoveDesignationArgs = {
  errors: null | unknown[];
  meetingId: string;
  designationId: string;
};
type NewDesignationArgs = {
  errors: null | unknown[];
  meetingId: string;
  documentId: string;
  newDesignation: {
    id: string;
  };
};
type MQuery = {
  meeting: {
    documentBundle: {
      documents: {
        edges: { node: DocNode }[];
      };
    };
  };
};
type MQueryVars = { meetingId: string };
type DocNode = {
  id: string;
  annotationDesignations: {
    edges: { node: { id: string } }[];
  };
};

export function removeDesignationFromDocumentCache(
  query: DocumentNode,
  cacheProxy: ApolloCache<unknown>,
  args: RemoveDesignationArgs,
) {
  const { errors, meetingId, designationId } = args;
  if (errors?.length) {
    return;
  }
  const { meeting, ...otherData } = cacheProxy.readQuery<MQuery, MQueryVars>({
    query,
    variables: { meetingId },
  })!;
  const newMeeting = updateDocumentNode(
    meeting,
    (docNode: DocNode) =>
      docNode.annotationDesignations.edges.find((edge) => edge.node.id === designationId),
    (docNode: DocNode) => ({
      ...docNode,
      annotationDesignations: {
        ...docNode.annotationDesignations,
        edges: docNode.annotationDesignations.edges.filter(
          (edge) => edge.node.id !== designationId,
        ),
      },
    }),
  );
  cacheProxy.writeQuery({
    query,
    data: { meeting: newMeeting, ...otherData },
    variables: { meetingId },
  });
}

export function addNewDesignationToDocumentCache(
  query: DocumentNode,
  cacheProxy: ApolloCache<unknown>,
  args: NewDesignationArgs,
) {
  const { errors, meetingId, documentId, newDesignation } = args;
  if (errors?.length) {
    return;
  }
  const { meeting, ...otherData } = cacheProxy.readQuery<MQuery, MQueryVars>({
    query,
    variables: { meetingId },
  })!;
  const newMeeting = updateDocumentNode(
    meeting,
    (docNode: DocNode) =>
      docNode.id === documentId &&
      // Its possible that this designat is already in the graph cache (due to a socket event racing with
      // a mutation completion, for instance). This check/protection makes the operation idempotent.
      !docNode.annotationDesignations.edges.find((edge) => edge.node.id === newDesignation.id),
    (docNode: DocNode) => ({
      ...docNode,
      annotationDesignations: {
        ...docNode.annotationDesignations,
        edges: [
          ...docNode.annotationDesignations.edges,
          {
            node: newDesignation,
            __typename: "AnnotationDesignationEdge",
          },
        ],
      },
    }),
  );
  cacheProxy.writeQuery({
    query,
    data: { meeting: newMeeting, ...otherData },
    variables: { meetingId },
  });
}
